import React, { useState, useEffect, useRef } from "react";
import { doc, onSnapshot, deleteDoc } from "firebase/firestore";
import { useNavContext } from "../context/NavContext";
import Nav from "./Nav";
import Swal from "sweetalert2";
import GeneradorModal from "./GeneradorModal";
import Spinner from "./Spinner";
import Paginador from "./Paginador";
import Modal from "react-modal";
import BandejaReporte from "./BandejaReporte";
import ResumenReporte from "./ResumenReporte";
import { useAuth } from "../context/AuthContext";
import { getSubCollection } from "../utils/getSubCollection";
import { useFunctionsPrevia } from "../hooks/useFunctionsPrevia";
import Breadcrumb from "./Breadcrumb";

// Estilos para el modal (igual que en CotizacionForm)
const styleForm = {
  content: {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    paddingLeft: 0,
    padding: 0,
    fontFamily: "Inter, sans-serif",
    "::WebkitScrollbar": {
      width: "0px",
      background: "transparent",
    },
    msOverflowStyle: "none",
    scrollbarWidth: "none",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 1008,
  },
};

const Generador = () => {
  const { searchTerm, setSearchTerm, proximasAVencer, proximosEventos } =
    useNavContext();
  const { user, teamOwnerId } = useAuth();
  const ownerUid = teamOwnerId || user.uid;
  const { formatDate } = useFunctionsPrevia();
  const [reportes, setReportes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReportes, setSelectedReportes] = useState([]);
  const [reporteEditando, setReporteEditando] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedReporteId, setSelectedReporteId] = useState(null);
  const [cloneMode, setCloneMode] = useState(false);
  const [resumenVisible, setResumenVisible] = useState(false);
  const [showBandeja, setShowBandeja] = useState(false);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [clientes, setClientes] = useState([]);
  const dropdownRef = useRef(null);

  // Cargar reportes desde Firestore (colección "obras")
  useEffect(() => {
    const obrasCol = getSubCollection(ownerUid, "obras");
    const unsub = onSnapshot(obrasCol, (snapshot) => {
      setReportes(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return () => unsub();
  }, [ownerUid]);

  useEffect(() => {
    // Subcolección de “clientes” para el teamOwner o usuario actual
    const clientesCol = getSubCollection(ownerUid, "clientes");

    const unsubscribe = onSnapshot(
      clientesCol,
      (snapshot) => {
        const fetched = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setClientes(fetched);
      },
      (error) => {
        console.error("Error real-time clientes:", error);
      }
    );

    return () => unsubscribe();
  }, [ownerUid]);

  // Cerrar menú de opciones al hacer clic fuera
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowOptionsMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Función para alternar menú de opciones
  const toggleOptionsMenu = () => {
    setShowOptionsMenu((prev) => !prev);
  };

  const handleCloneReport = (clon) => {
    setReporteEditando(clon); // <-- ponemos el clon en editingReport
    setCloneMode(true); // <-- activamos cloneMode
    setIsModalOpen(true); // <-- abrimos el modal

    // opcional: limpiamos cualquier sidebar/summary que quedase abierto
    setResumenVisible(false);
    setShowBandeja(false);
  };
  // Abrir modal "Nuevo" o "Editar"
  const handleOpenModal = () => {
    setReporteEditando(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setReporteEditando(null);
    setCloneMode(false);

    /* NUEVO: limpiar cualquier overlay lateral  */
    setShowBandeja(false); // oculta la bandeja
    setResumenVisible(false); // oculta el resumen
    setSelectedReporteId(null); // limpia la selección

    /* NUEVO: restaura la tabla a su estado normal */
    setSelectedReportes([]); // desmarca los check-boxes
    setShowOptionsMenu(false); // cierra menú “⋮” si estuviera abierto
  };

  // Seleccionar/deseleccionar reportes (checkbox)
  const handleSelectReporte = (reporteId) => {
    setSelectedReportes((prevSelected) => {
      let newSelected;
      if (prevSelected.includes(reporteId)) {
        newSelected = prevSelected.filter((id) => id !== reporteId);
      } else {
        newSelected = [...prevSelected, reporteId];
      }
      setShowOptionsMenu(newSelected.length > 0);
      return newSelected;
    });
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIds = filteredReportes.map((rep) => rep.id);
      setSelectedReportes(allIds);
      setShowOptionsMenu(allIds.length > 0);
    } else {
      setSelectedReportes([]);
      setShowOptionsMenu(false);
    }
  };

  const normalizeText = (text) =>
    (text || "")
      .toString()
      .normalize("NFD") // Normaliza caracteres Unicode (acentos)
      .replace(/[\u0300-\u036f]/g, "") // Elimina acentos
      .toLowerCase()
      .trim();

  const filteredReportes = React.useMemo(() => {
    if (!reportes || reportes.length === 0) return [];
    const searchLower = normalizeText(searchTerm);

    return reportes.filter((reporte) => {
      const searchableFields = [
        normalizeText(reporte.nombreCliente),
        normalizeText(reporte.numeroReporte),
        normalizeText(reporte.referencia),
        normalizeText(reporte.ordenCompra),
        normalizeText(reporte.usuario),
        normalizeText(reporte.vendedor),
        normalizeText(reporte.planta),
        normalizeText(reporte.area),
        normalizeText(reporte.divisa),
        normalizeText(reporte.total),
      ];

      const matchesText = searchableFields.some((field) =>
        field.includes(searchLower)
      );

      let matchesDate = false;
      if (reporte.fechaReporte) {
        const date = new Date(reporte.fechaReporte);
        const day = date.getDate().toString().padStart(2, "0");
        const months = [
          "enero",
          "febrero",
          "marzo",
          "abril",
          "mayo",
          "junio",
          "julio",
          "agosto",
          "septiembre",
          "octubre",
          "noviembre",
          "diciembre",
        ];
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        const formattedDate = `${day} ${month} ${year}`;
        matchesDate = normalizeText(formattedDate).includes(searchLower);
      }

      return matchesText || matchesDate;
    });
  }, [reportes, searchTerm]);

  const totalReportes = filteredReportes.length;

  // Paginación
  const handleItemsPerPageChange = (items) => setItemsPerPage(items);
  const handlePageChange = (page) => setCurrentPage(page);

  // Abrir vista de resumen del reporte
  const handleRowClickReporte = (reportId) => {
    setSelectedReporteId(reportId);
    setResumenVisible(true);
    setShowBandeja(true);
  };

  // Eliminar reportes seleccionados
  const handleDeleteSelected = async () => {
    if (!selectedReportes.length) return; // nada que eliminar

    const { isConfirmed } = await Swal.fire({
      title: "¿Estás seguro?",
      text: "Esta acción eliminará los reportes seleccionados.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      scrollbarPadding: false,
    });

    if (!isConfirmed) return;

    try {
      // Utilizamos la subcolección para aislar por ownerUid
      const obrasCol = getSubCollection(ownerUid, "obras");
      // Eliminamos todos en paralelo
      await Promise.all(
        selectedReportes.map((id) => deleteDoc(doc(obrasCol, id)))
      );

      // Limpiamos selección y ocultamos el menú de opciones
      setSelectedReportes([]);
      setShowOptionsMenu(false);

      await Swal.fire({
        title: "¡Reporte eliminado!",
        text: "Los reportes seleccionados han sido eliminados correctamente.",
        icon: "success",
        confirmButtonColor: "#3085d6",
        confirmButtonText: "Ok",
        scrollbarPadding: false,
      });
      // El listener en tiempo real refrescará `reportes`
    } catch (error) {
      console.error("Error al eliminar reportes:", error);
      Swal.fire({
        title: "Error",
        text: "Hubo un problema al eliminar los reportes. Inténtalo de nuevo.",
        icon: "error",
        confirmButtonText: "Ok",
        scrollbarPadding: false,
      });
    }
  };

  // Editar (sólo uno a la vez)
  const handleEditSelected = () => {
    if (selectedReportes.length === 0) {
      Swal.fire("Atención", "No has seleccionado ningún reporte.", "info");
      return;
    }
    if (selectedReportes.length > 1) {
      Swal.fire("Atención", "Selecciona solo un reporte para editar.", "info");
      return;
    }
    const reportToEdit = reportes.find((rep) => rep.id === selectedReportes[0]);
    if (!reportToEdit) {
      Swal.fire("Error", "No se encontró el reporte a editar.", "error");
      return;
    }
    setReporteEditando(reportToEdit);
    setIsModalOpen(true);
    setShowOptionsMenu(false);
  };

  // Paginación: separar reportes en páginas
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedReportes = filteredReportes.slice(startIndex, endIndex);

  return (
    <>
      <div className="no-sticky">
        {!showBandeja && !resumenVisible && !isModalOpen && (
          <>
            <Nav
              handleSearch={setSearchTerm}
              proximasAVencer={proximasAVencer}
              proximosEventos={proximosEventos}
            />

            <div className="task-filters" style={{ flexWrap: "wrap" }}>
              <div
                style={{
                  flexBasis: "100%", // ocupa toda la fila
                  padding: "0 4px", // mismo gutter que usas
                  margin: "0 0 5px", // respirito arriba/abajo
                }}
              >
                <Breadcrumb />
              </div>
              <div className="header-actions-proyectos">
                <button className="action-button-proyectos">
                  <i className="bi bi-funnel-fill"></i> Todos
                  <i
                    className="bi bi-chevron-down"
                    style={{ fontSize: "12px" }}
                  ></i>
                </button>
                <div className="buscador-pedidos">
                  <i className="bi bi-search"></i>
                  <input
                    type="text"
                    className="buscador-input"
                    placeholder="Buscar reportes..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              <div className="task-filters-right">
                <div
                  className="view-buttons-container"
                  style={{ position: "relative" }}
                >
                  <button className="view-button" onClick={toggleOptionsMenu}>
                    <i className="bi bi-three-dots-vertical"></i>
                  </button>

                  <button className="exportar-button">
                    <i
                      className="bi bi-cloud-arrow-down"
                      style={{ marginRight: "5px" }}
                    ></i>
                    Exportar
                  </button>

                  {showOptionsMenu && (
                    <div className="dropdown-options" ref={dropdownRef}>
                      {selectedReportes.length > 0 ? (
                        <>
                          <p
                            onClick={handleDeleteSelected}
                            style={{
                              margin: 0,
                              padding: "8px 12px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <i
                              className="bi bi-trash"
                              style={{ marginRight: "8px" }}
                            ></i>
                            Eliminar
                          </p>
                          <p
                            onClick={handleEditSelected}
                            style={{
                              margin: 0,
                              padding: "8px 12px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <i
                              className="bi bi-pencil-square"
                              style={{ marginRight: "8px" }}
                            ></i>
                            Editar
                          </p>
                        </>
                      ) : (
                        <>
                          <p
                            onClick={() => {
                              Swal.fire(
                                "Importar",
                                "Función de importar...",
                                "info"
                              );
                              setShowOptionsMenu(false);
                            }}
                            style={{
                              margin: 0,
                              padding: "8px 12px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <i
                              className="bi bi-box-arrow-in-down"
                              style={{ marginRight: "8px" }}
                            ></i>
                            Importar
                          </p>
                          <p
                            onClick={() => {
                              Swal.fire(
                                "Exportar",
                                "Función de exportar...",
                                "info"
                              );
                              setShowOptionsMenu(false);
                            }}
                            style={{
                              margin: 0,
                              padding: "8px 12px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <i
                              className="bi bi-box-arrow-up"
                              style={{ marginRight: "8px" }}
                            ></i>
                            Exportar
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <button className="new-task-button" onClick={handleOpenModal}>
                    + Nuevo
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        <div className="cotizaciones-table">
          {/* Si se muestra la bandeja, renderizamos el componente BandejaReporte */}
          {showBandeja && (
            <BandejaReporte
              reportes={reportes}
              onRowClick={handleRowClickReporte}
              selectedReporteId={selectedReporteId}
              // Puedes pasar más props según lo necesites
            />
          )}

          {/* Si no se muestra la bandeja, renderizamos la tabla */}
          {!showBandeja && (
            <div className="tabla-cotizaciones-wrapper">
              <div className="tabla-cotizaciones-content">
                {loading ? (
                  <Spinner />
                ) : reportes.length === 0 ? (
                  // 1️⃣ Aún no hay ningún reporte
                  <div className="no-resultados">
                    <div
                      className="mensaje-sin-resultados"
                      style={{ marginTop: "30px" }}
                    >
                      <p style={{ fontSize: "14px" }}>
                        Aún no hay reportes todavía.
                      </p>
                    </div>
                    <div className="imagen-sin-resultados">
                      <img
                        src="/img/no-data.svg"
                        alt="Sin reportes"
                        width={350}
                        height={350}
                      />
                    </div>
                  </div>
                ) : filteredReportes.length > 0 ? (
                  // 2️⃣ Hay reportes y alguno coincide con la búsqueda
                  <table>
                    <thead>
                      <tr>
                        <th>
                          <input
                            type="checkbox"
                            onChange={handleSelectAll}
                            className="checkbox-select-all check"
                            checked={
                              selectedReportes.length ===
                              filteredReportes.length
                            }
                          />
                        </th>
                        <th>Fecha</th>
                        <th>No.</th>
                        <th>Referencia</th>
                        <th>Cliente</th>
                        <th>Importe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedReportes.map((report) => (
                        <tr key={report.id}>
                          <td
                            className="check"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={selectedReportes.includes(report.id)}
                              onChange={() => handleSelectReporte(report.id)}
                              className="checkbox"
                            />
                          </td>
                          <td onClick={() => handleRowClickReporte(report.id)}>
                            <div className="estilos-fechas">
                              {formatDate(report.fechaReporte)}
                            </div>
                          </td>
                          <td
                            style={{ color: "#007bff", fontWeight: "500" }}
                            onClick={() => handleRowClickReporte(report.id)}
                          >
                            {report.numeroObra?.toString().padStart(4, "0")}
                          </td>
                          <td onClick={() => handleRowClickReporte(report.id)}>
                            {report.referencia}
                          </td>
                          <td onClick={() => handleRowClickReporte(report.id)}>
                            {report.nombreCliente}
                          </td>
                          <td
                            className="importe"
                            onClick={() => handleRowClickReporte(report.id)}
                          >
                            {report.divisa}{" "}
                            {parseFloat(report.total || 0)
                              .toFixed(2)
                              .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  // 3️⃣ Hay reportes, pero ninguno coincide con el término de búsqueda
                  <div className="no-resultados">
                    <div
                      className="mensaje-sin-resultados"
                      style={{ marginTop: "30px" }}
                    >
                      <p style={{ fontSize: "14px" }}>
                        No hay reportes que coincidan con tu búsqueda.
                      </p>
                    </div>
                    <div className="imagen-sin-resultados">
                      <img
                        src="/img/noSearch.svg"
                        alt="Sin resultados"
                        className="sin-resultados-imagen"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="tabla-cotizaciones-footer">
                <div className="paginador-container">
                  <Paginador
                    totalItems={totalReportes}
                    itemsPerPage={itemsPerPage}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handleItemsPerPageChange}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Resumen a la derecha */}
          <div
            className={`resumen-container ${selectedReporteId ? "active" : ""}`}
          >
            <ResumenReporte
              reporte={reportes.find((r) => r.id === selectedReporteId)}
              isOpen={resumenVisible}
              onClose={() => {
                setResumenVisible(false);
                setShowBandeja(false);
                setSelectedReporteId(null);
              }}
              setReportes={setReportes}
              reportes={selectedReporteId}
              onClonar={handleCloneReport}
              clientes={clientes}
            />
          </div>
        </div>

        {/* Modal para crear/editar reporte */}
        <Modal
          isOpen={isModalOpen}
          contentLabel="Generador Modal"
          style={styleForm}
        >
          <button onClick={handleCloseModal} className="cerrar-button">
            <i className="bi bi-x-lg"></i>
          </button>
          <GeneradorModal
            editingReport={reporteEditando}
            cloneMode={cloneMode}
            onClose={handleCloseModal}
            reportes={reportes}
          />
        </Modal>
      </div>
    </>
  );
};

export default Generador;
